/**
 * The `kuutio-split` data block: the numbers a synthetic target-group agent
 * (Kuutio-agents, kind "opinion") stands on, as one fenced JSON block the chat
 * draws as split bars (specs/kuutio-agents/README.md v1, "charts").
 *
 *   ```kuutio-split
 *   { "title": "…",
 *     "groups": [{ "key": "A", "label": "Ryhmä A" }, { "key": "B", "label": "Ryhmä B" }, { "key": "C", "label": "Ryhmä C" }],
 *     "rows": [{ "ref": "V39", "statement": "…", "A": 96, "B": 26, "C": 51, "all": 65 }] }
 *   ```
 *
 * Two groups or more. Row values are agree percents; `all` is optional. A
 * group's size is not part of the block: a `share` written by an older prompt
 * is dropped, so no chart shows how big a group is. `ref` is the marker the row cites —
 * the base ref (V39) in a voice's own answer, the numbered marker (44) in a
 * report — so the chart and the sources list agree. Pure: no I/O, no React.
 *
 * Canonical copy lives in packages/shared/splitBlock.js (the display imports
 * that one); apps/backend/src/shared/splitBlock.js is a duplicate because the
 * backend Docker image cannot reach packages/ — same arrangement as
 * effectTypes.js. A backend test asserts the two files are identical.
 */

export const SPLIT_FENCE = 'kuutio-split';
export const MAX_SPLIT_ROWS = 8;
const REF = /^([SPLRVNODG]\d{1,3}|\d{1,3})$/i;
const FENCE = /```kuutio-split[^\n]*\n([\s\S]*?)```/g;

const pct = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? Math.round(n * 10) / 10 : undefined;
};
const str = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim());

/**
 * Parse the body of one block. Returns { block, errors }: block is the
 * normalised data when errors is empty, null otherwise. Never throws.
 */
export function parseSplitBlock(body) {
  const errors = [];
  let raw;
  try { raw = JSON.parse(String(body ?? '').trim()); } catch (e) { return { block: null, errors: [`not JSON: ${e.message}`] }; }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { block: null, errors: ['not a JSON object'] };

  const groups = (Array.isArray(raw.groups) ? raw.groups : []).map((g, i) => {
    const key = str(g?.key);
    if (!key) errors.push(`group ${i + 1}: key missing`);
    return { key, label: str(g?.label) || key };
  });
  if (groups.length < 2) errors.push('needs at least two groups');
  if (new Set(groups.map((g) => g.key)).size !== groups.length) errors.push('group keys must be unique');

  const rawRows = Array.isArray(raw.rows) ? raw.rows : [];
  if (!rawRows.length) errors.push('needs at least one row');
  if (rawRows.length > MAX_SPLIT_ROWS) errors.push(`at most ${MAX_SPLIT_ROWS} rows`);
  const rows = rawRows.slice(0, MAX_SPLIT_ROWS).map((r, i) => {
    const ref = str(r?.ref).toUpperCase();
    const statement = str(r?.statement);
    if (!REF.test(ref)) errors.push(`row ${i + 1}: ref must be a base ref (V39) or a marker number`);
    if (!statement) errors.push(`row ${i + 1}: statement missing`);
    const shares = {};
    for (const g of groups) {
      const v = pct(r?.[g.key]);
      if (v == null) errors.push(`row ${i + 1}: share for group ${g.key} missing or not a percent`);
      shares[g.key] = v ?? 0;
    }
    const all = pct(r?.all);
    if (all === undefined) errors.push(`row ${i + 1}: all must be a percent`);
    return { ref, statement, shares, all: all ?? null };
  });

  if (errors.length) return { block: null, errors };
  return { block: { title: str(raw.title) || null, groups, rows }, errors };
}

/** Every kuutio-split fence in a Markdown text, parsed, in order (invalid ones carry their errors). */
export function extractSplitBlocks(markdown) {
  const out = [];
  for (const m of String(markdown || '').matchAll(FENCE)) out.push({ body: m[1], ...parseSplitBlock(m[1]) });
  return out;
}

/** Rows whose ref is not among the known refs (base refs or marker numbers), so a chart never cites what the sources do not. */
export function unknownSplitRefs(block, knownRefs) {
  const known = new Set((knownRefs || []).map((r) => String(r).toUpperCase()));
  return (block?.rows || []).map((r) => r.ref).filter((ref) => !known.has(ref));
}
